// cesium-camera.ts

import {
  Cartesian2,
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  Color,
  HeadingPitchRange,
  Rectangle,
} from "cesium";
import type {
  CameraOptions,
  CesiumViewSettings,
  InitializeCesiumOptions,
} from "./cesium-types";

// Note: getViewer is imported dynamically to avoid circular imports

// State management for camera operations
let initialFlightStarted = false;
let isViewerReady = false;
let initialCameraSetRef = false;
const cameraReady = false;
let lastCameraPosition: CesiumViewSettings | null = null;
const DEBOUNCE_DELAY = 150; // 150ms delay for camera move events
let debounceTimer: any = null;

// Helper function to ensure globe visibility
export function ensureGlobeVisibility(viewer: any) {
  if (!viewer || initialFlightStarted) return;
  viewer.scene.globe.baseColor = Color.TRANSPARENT;
  viewer.scene.backgroundColor = Color.TRANSPARENT;
  initialFlightStarted = true;
}

// Helper function to create camera destination and orientation
export function createCameraOptions(
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
): CameraOptions {
  const height = Math.max(100, settings.height || 1000);

  return {
    destination: Cartesian3.fromDegrees(
      settings.center[0],
      settings.center[1],
      height,
    ),
    orientation: {
      heading: settings.bearing ? CesiumMath.toRadians(settings.bearing) : 0,
      pitch: settings.pitch
        ? CesiumMath.toRadians(settings.pitch)
        : -CesiumMath.PI_OVER_TWO,
      roll: 0.0,
    },
    duration: duration,
    complete: onComplete,
  };
}

// Convert current camera position to CesiumViewSettings
export function getCurrentCameraPosition(
  viewer: any,
): CesiumViewSettings | null {
  if (!viewer) return null;

  const cartographic = Cartographic.fromCartesian(viewer.camera.position);
  return {
    center: [
      CesiumMath.toDegrees(cartographic.longitude),
      CesiumMath.toDegrees(cartographic.latitude),
    ],
    height: cartographic.height,
    bearing: CesiumMath.toDegrees(viewer.camera.heading),
    pitch: CesiumMath.toDegrees(viewer.camera.pitch),
  };
}

// Main function for shouldFlyTo pattern (matches CesiumMap exactly)
export function flyToPosition(
  flyToSettings: CesiumViewSettings,
  cesiumCameraPosition?: CesiumViewSettings,
  viewer?: any,
) {
  // Match CesiumMap's exact condition check
  if (
    !isViewerReady ||
    !initialCameraSetRef ||
    !viewer ||
    !cesiumCameraPosition ||
    !flyToSettings
  ) {
    return;
  }

  // Add 10ms delay before flying (exactly like CesiumMap)
  setTimeout(() => {
    if (!viewer) return;

    const cameraOptions = createCameraOptions(flyToSettings, 2.0);
    viewer.camera.flyTo(cameraOptions);
  }, 10);
}

// Unified camera transition function
export function flyToView(
  viewer: any,
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  if (!viewer) return;

  ensureGlobeVisibility(viewer);

  // Use setTimeout like CesiumMap for consistent behavior
  setTimeout(() => {
    if (!viewer) return;

    const cameraOptions = createCameraOptions(settings, duration, onComplete);
    viewer.camera.flyTo(cameraOptions);
  }, 10);
}

// Alias for backward compatibility
export function updateCameraPosition(
  viewer: any,
  settings: CesiumViewSettings,
  duration = 2.0,
  onComplete?: () => void,
) {
  flyToView(viewer, settings, duration, onComplete);
}

// Set camera position immediately (without animation)
export function setCameraPosition(viewer: any, settings: CesiumViewSettings) {
  if (!viewer) return;

  ensureGlobeVisibility(viewer);

  const cameraOptions = createCameraOptions(settings);
  viewer.camera.setView({
    destination: cameraOptions.destination,
    orientation: cameraOptions.orientation,
  });
}

// Function to check and update tileset visibility based on camera height
function updateTilesetVisibility(
  viewer: any,
  tilesetRegistry: Map<string | number, any>,
) {
  if (!viewer || !tilesetRegistry) return;

  const cameraHeight = viewer.camera.positionCartographic.height;
  const isVisible = cameraHeight < 1000; // Visible if camera is below 1000 meters

  tilesetRegistry.forEach((tileset) => {
    if (tileset.show !== isVisible) {
      tileset.show = isVisible;
    }
  });
}

// Setup camera event listeners
export function setupCameraEvents(
  viewer: any,
  tilesetRegistry: Map<string | number, any>,
  onCameraMove?: (position: CesiumViewSettings) => void,
  onViewCenterChange?: InitializeCesiumOptions["onViewCenterChange"],
) {
  if (!viewer) return;

  const handleCameraMove = () => {
    // Update tileset visibility on every camera move
    updateTilesetVisibility(viewer, tilesetRegistry);

    // Debounce the onCameraMove callback for performance
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const currentPosition = getCurrentCameraPosition(viewer);
      if (
        currentPosition &&
        JSON.stringify(currentPosition) !== JSON.stringify(lastCameraPosition)
      ) {
        lastCameraPosition = currentPosition;
        onCameraMove?.(currentPosition);
      }
    }, DEBOUNCE_DELAY);
  };

  const handleCenterChange = () => {
    if (!onViewCenterChange) return;

    const centerScreenPosition = new Cartesian2(
      viewer.canvas.clientWidth / 2,
      viewer.canvas.clientHeight / 2,
    );

    const centerPosition = viewer.scene.pickPosition(centerScreenPosition);

    if (centerPosition) {
      const centerCartographic = Cartographic.fromCartesian(centerPosition);
      const centerLongitude = CesiumMath.toDegrees(
        centerCartographic.longitude,
      );
      const centerLatitude = CesiumMath.toDegrees(centerCartographic.latitude);

      onViewCenterChange({
        latitude: centerLatitude,
        longitude: centerLongitude,
      });
    }
  };

  viewer.camera.moveEnd.addEventListener(handleCameraMove);
  viewer.camera.moveEnd.addEventListener(handleCenterChange);
}

// Function to fly the camera to a specific feature (entity)
export function flyToFeature(viewer: any, entity: any, duration = 1.5) {
  if (!viewer || !entity) {
    return;
  }

  const heading = viewer.camera.heading;
  const pitch = -0.6;

  // If the entity has a polygon or polyline, we can compute a bounding sphere
  if (entity.polygon || entity.polyline) {
    viewer.flyTo(entity, {
      duration,
      offset: new HeadingPitchRange(heading, pitch, 0),
    });
  } else if (entity.position) {
    // For point features, fly to the entity with a specific offset
    viewer.flyTo(entity, {
      duration,
      offset: new HeadingPitchRange(heading, pitch, 800), // Use offset for better viewing distance
    });
  }
}

// Camera state management functions
export function setCameraReady(ready: boolean) {
  isViewerReady = ready;
  initialCameraSetRef = ready;
}

export function resetCameraState() {
  initialFlightStarted = false;
  isViewerReady = false;
  initialCameraSetRef = false;
}

export function getCameraState() {
  return {
    initialFlightStarted,
    isViewerReady,
    initialCameraSetRef,
  };
}
